import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  FormAnswer,
  FormAnswerPayload,
  FormInstance,
  FormInstanceAnswers,
  FormInstanceApi,
  FormInstancesByProject,
  FormTemplateAnswers,
} from 'api/form';
import { ProjectId } from 'api/models';
import { invalidateTaskQuery } from 'utils/invalidate-task-query';

const baseOptions = {
  retry: 1,
  refetchOnWindowFocus: false,
  staleTime: 0,
  cacheTime: 0,
};

export const getQueryFormInstanceByIdKey = (instanceId: number) => [
  'FORM_INSTANCE',
  instanceId,
];
export const getQueryFormAnswerByIdKey = (answerId: number) => [
  'FORM_INSTANCE_ANSWER',
  answerId,
];
export const useQueryFormInstanceById = (
  instanceId: number,
  options: UseQueryOptions<
    FormInstance,
    unknown,
    FormInstance,
    (string | number)[]
  > = {},
): UseQueryResult<FormInstance, unknown> =>
  useQuery(
    getQueryFormInstanceByIdKey(instanceId),
    () => FormInstanceApi.getFormInstanceById(instanceId),
    {
      ...baseOptions,
      ...options,
    },
  );

export const getQueryFormInstanceByProjectKey = ([_, projectId]: ProjectId) => [
  'FORM_INSTANCE_BY_PROJECT',
  projectId,
];
export const useQueryFormInstanceByProjectKey = (
  projectId: ProjectId,
  options: UseQueryOptions<
    FormInstancesByProject,
    unknown,
    FormInstancesByProject,
    (string | number)[]
  > = {},
): UseQueryResult<FormInstancesByProject, unknown> =>
  useQuery(
    getQueryFormInstanceByProjectKey(projectId),
    () => FormInstanceApi.getFormInstancesByProject(projectId),
    {
      ...baseOptions,
      ...options,
    },
  );

export const getQueryAllAnswersByFormInstanceKey = (instanceId: number) => [
  'FORM_INSTANCE_ALL_ANSWERS',
  instanceId,
];
export const useQueryAllAnswersByFormInstanceKey = (
  instanceId: number,
  options: UseQueryOptions<
    FormInstanceAnswers,
    unknown,
    FormInstanceAnswers,
    (string | number)[]
  > = {},
): UseQueryResult<FormInstanceAnswers, unknown> =>
  useQuery(
    getQueryAllAnswersByFormInstanceKey(instanceId),
    () => FormInstanceApi.getAllAnswersByFormInstance(instanceId),
    {
      ...baseOptions,
      ...options,
    },
  );

export const getQueryAllAnswersByFormTemplateKey = (formTemplateId: number) => [
  'FORM_TEMPLATE_ALL_ANSWERS',
  formTemplateId,
];
export const useQueryAllAnswersByFormTemplate = (
  formTemplateId: number,
  options: UseQueryOptions<
    FormTemplateAnswers,
    unknown,
    FormTemplateAnswers,
    (string | number)[]
  > = {},
): UseQueryResult<FormTemplateAnswers, unknown> =>
  useQuery(
    getQueryAllAnswersByFormTemplateKey(formTemplateId),
    () => FormInstanceApi.getAllAnswersByFormTemplate(formTemplateId),
    {
      ...baseOptions,
      ...options,
    },
  );

export interface FormInstanceDetails {
  instances: {
    formInstanceId: number;
    taskName: string;
  }[];
}

export const getQueryAllInstancesByFormTemplateKey = (
  formTemplateId: number,
) => ['FORM_TEMPLATE_ALL_INSTANCES', formTemplateId];

export const useQueryAllInstancesByFormTemplate = (
  formTemplateId: number,
  options: UseQueryOptions<
    FormInstanceDetails,
    unknown,
    FormInstanceDetails,
    (string | number)[]
  > = {},
): UseQueryResult<FormInstanceDetails, unknown> =>
  useQuery(
    getQueryAllInstancesByFormTemplateKey(formTemplateId),
    () => FormInstanceApi.getAllInstancesByFormTemplate(formTemplateId),
    {
      ...baseOptions,
      ...options,
    },
  );

export const useFormAnswerQuery = (
  answerId: number,
  options: UseQueryOptions<
    FormAnswer,
    unknown,
    FormAnswer,
    (string | number)[]
  > = {},
): UseQueryResult<FormAnswer, unknown> =>
  useQuery(
    getQueryFormAnswerByIdKey(answerId),
    () => FormInstanceApi.getFormAnswerById(answerId),
    {
      ...baseOptions,
      ...options,
    },
  );

export interface UploadFormAnswerImagePayload {
  formAnswerId?: number;
  payload: FormAnswerPayload;
}

export const useCreateUpdateFormAnswer = ({
  taskId,
  projectId: _projectId,
  onAnswerSubmitSuccess,
  queryConfig = {},
}: {
  taskId: number;
  projectId: number;
  onAnswerSubmitSuccess: (response: FormAnswer) => void;
  queryConfig: UseQueryOptions<FormAnswer, unknown, FormAnswer>;
}): UseMutationResult<FormAnswer, unknown, UploadFormAnswerImagePayload> => {
  const queryClient = useQueryClient();

  return useMutation<FormAnswer, unknown, UploadFormAnswerImagePayload>(
    ({ formAnswerId, payload }) => {
      if (formAnswerId) {
        return FormInstanceApi.updateFormAnswer(formAnswerId, payload);
      }

      return FormInstanceApi.createFormAnswer(payload);
    },
    {
      onSuccess: async (response) => {
        await queryClient.invalidateQueries(
          getQueryFormAnswerByIdKey(response.answerId),
        );
        await invalidateTaskQuery(taskId);
        onAnswerSubmitSuccess(response);
      },
      ...queryConfig,
    },
  );
};
